import React from 'react';

import styled from '@emotion/styled';

import {
  COLORS,
  SANS_SERIF_FONT_FAMILY,
  SERIF_FONT_FAMILY,
  mq,
} from '@clutter/clean';

import { FunnelEntryPoint } from '@shared/funnel_entry_point';
import { FunnelEntryPointForm } from '@shared/funnel_entry_point_form';
import { FunnelEntryPointZipInput } from '@shared/funnel_entry_point_zip_input';
import { ServiceSelection } from '@shared/service_selector/constants';
import { TransparentButton } from '@shared/transparent_button';
import { TopZipToast } from '@shared/zip_error_toast';

import arrowForward from './nav_bar_images/arrow_forward.svg';
import xIcon from './nav_bar_images/x.svg';

import { HamburgerToggle } from '@images/svg/hamburger';
import { Logo } from '@images/svg/logo';

const NavContainer = styled.div<{
  scrolled: boolean;
  fixedTopOffsetPx?: number;
}>`
  background: ${COLORS.cloud};
  border-bottom: 1px solid ${COLORS.grayBorder};
  padding: 18px 60px;
  min-height: 72px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: fixed;
  top: ${(p) => `${p.fixedTopOffsetPx ?? 0}px`};
  transition: top 0.22s ease;
  left: 0;
  right: 0;
  width: 100%;
  z-index: 400;
  backdrop-filter: ${(p) => (p.scrolled ? 'saturate(180%) blur(6px)' : 'none')};
  background: ${(p) => (p.scrolled ? 'rgba(255,255,255,0.92)' : COLORS.cloud)};
  box-shadow: ${(p) =>
    p.scrolled ? '0 2px 12px rgba(0,0,0,0.1)' : '0 1px 3px rgba(0,0,0,0.1)'};

  ${mq({ padding: ['18px 24px', '18px 60px'] })}
`;

const ClutterLogo = styled(Logo)`
  ${mq({ width: ['84px', '128px'] })}
`;

const MenuButton = styled(TransparentButton)`
  & > div {
    width: 24px;
    height: 18px;
  }
`;

const RightGroup = styled.div<{ hideOnMobile?: boolean }>`
  display: flex;
  align-items: center;
  gap: 16px;
  ${(p) =>
    p.hideOnMobile &&
    mq({
      display: ['none', 'none', 'flex'],
    })}
`;

const LeftGroup = styled.div<{ hideOnMobile?: boolean }>`
  ${(p) =>
    p.hideOnMobile &&
    mq({
      display: ['none', 'none', 'block'],
    })}
`;

const CenterZip = styled.div<{ showOnMobile?: boolean }>`
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  width: calc(100% - 32px);
  max-width: 566px;
  display: none;
  ${mq({ display: ['none', 'block'] })}
  ${(p) =>
    p.showOnMobile &&
    mq({
      display: ['block', 'block'],
    })}
`;

const ZipRow = styled.div`
  display: flex;
  align-items: center;
  gap: 18px;
  border: none;
  border-radius: 999px;
  background: #f3f3f3;
  height: 48px;
  padding: 8px 24px;

  /* Ensure the shared Input matches the gray container */
  input {
    width: 100%;
    height: 100%;
    border: none !important;
    background: transparent !important;
    outline: none !important;
    box-shadow: none !important;
    font-size: 18px;
    line-height: 27px;
    color: ${COLORS.storm};
    font-family: ${SANS_SERIF_FONT_FAMILY};
    font-weight: 500;
  }

  input::placeholder {
    color: ${COLORS.storm};
    opacity: 1;
  }

  /* Autofill fixes to keep input matching the gray container */
  input:-webkit-autofill,
  input:-webkit-autofill:hover,
  input:-webkit-autofill:focus {
    -webkit-text-fill-color: ${COLORS.storm} !important;
    -webkit-box-shadow: 0 0 0px 1000px #f3f3f3 inset !important;
    box-shadow: 0 0 0px 1000px #f3f3f3 inset !important;
    background-color: #f3f3f3 !important;
    caret-color: ${COLORS.storm} !important;
    border-radius: 999px !important;
    -webkit-background-clip: padding-box;
    background-clip: padding-box;
    transition: background-color 99999s ease-in-out 0s;
  }

  input:autofill,
  input:autofill:hover,
  input:autofill:focus {
    -webkit-text-fill-color: ${COLORS.storm} !important;
    -webkit-box-shadow: 0 0 0px 1000px #f3f3f3 inset !important;
    box-shadow: 0 0 0px 1000px #f3f3f3 inset !important;
    background-color: #f3f3f3 !important;
    caret-color: ${COLORS.storm} !important;
    border-radius: 999px !important;
    -webkit-background-clip: padding-box;
    background-clip: padding-box;
    transition: background-color 99999s ease-in-out 0s;
  }
`;

// Input styling is provided by the shared FunnelEntryPointZipInput

const CircleButton = styled.button`
  appearance: none;
  border: none;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: ${COLORS.tealPrimary};
  color: ${COLORS.cloud};
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  img {
    display: block;
    filter: brightness(0) invert(1);
  }
`;

const Backdrop = styled.div<{ hidden: boolean }>`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.3);
  display: ${(p) => (p.hidden ? 'none' : 'block')};
  z-index: 410;
`;

const SidePanel = styled.aside<{ open: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 320px;
  background: ${COLORS.cloud};
  box-shadow: -2px 0 20px rgba(0, 0, 0, 0.1);
  transform: translateX(${(p) => (p.open ? '0%' : '100%')});
  transition: transform 0.25s ease-out;
  z-index: 415;
  display: flex;
  flex-direction: column;
`;

const PanelHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 18px 24px;
`;

const CloseButton = styled.button`
  background: transparent;
  border: none;
  color: ${COLORS.panther};
  line-height: 0;
  cursor: pointer;
`;

const Menu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 24px;
`;

const MenuItem = styled.a`
  color: ${COLORS.panther};
  font-family: ${SERIF_FONT_FAMILY};
  font-weight: 500;
  font-size: 36px;
  line-height: 43.2px;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

type NavBarProps = {
  scrolled: boolean;
  showZipAfterSnap?: boolean;
  /** When a fixed strip (e.g. promo) sits above the nav, pass its height in px. */
  fixedTopOffsetPx?: number;
};

export const NavBar: React.FC<NavBarProps> = ({
  scrolled,
  showZipAfterSnap,
  fixedTopOffsetPx,
}) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const menuButtonRef = React.useRef<HTMLButtonElement | null>(null);
  const firstItemRef = React.useRef<HTMLAnchorElement | null>(null);

  const didMountRef = React.useRef(false);

  React.useEffect(() => {
    // Prevent auto-focus styles on initial mount/refresh
    if (!didMountRef.current) {
      didMountRef.current = true;
      return;
    }
    if (menuOpen) {
      // Focus first interactive element inside the panel
      firstItemRef.current?.focus();
    } else {
      // Return focus to the toggle button
      menuButtonRef.current?.focus();
    }
  }, [menuOpen]);

  const onMenuKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      e.stopPropagation();
      setMenuOpen(false);
    }
  };

  return (
    <>
      <NavContainer scrolled={scrolled} fixedTopOffsetPx={fixedTopOffsetPx}>
        <LeftGroup hideOnMobile={scrolled && !!showZipAfterSnap}>
          <a href="/">
            <ClutterLogo fillColor={COLORS.tealPrimary} />
          </a>
        </LeftGroup>
        {scrolled && showZipAfterSnap && (
          <CenterZip showOnMobile>
            <FunnelEntryPoint
              service={ServiceSelection.Storage}
              wtConfig={{ container: 'navbar' }}
            >
              <TopZipToast />
              <FunnelEntryPointForm id="navbar_funnel_form">
                <ZipRow>
                  <div style={{ flex: 1 }}>
                    <FunnelEntryPointZipInput shortenPlaceholderText />
                  </div>
                  <CircleButton type="submit" aria-label="Start">
                    <img src={arrowForward} width={18} height={18} alt="" />
                  </CircleButton>
                </ZipRow>
              </FunnelEntryPointForm>
            </FunnelEntryPoint>
          </CenterZip>
        )}
        <RightGroup
          hideOnMobile={scrolled && !!showZipAfterSnap}
          style={{ visibility: menuOpen ? 'hidden' : 'visible' }}
        >
          <MenuButton
            ref={menuButtonRef}
            aria-label={menuOpen ? 'Close Menu' : 'Open Menu'}
            aria-expanded={menuOpen}
            aria-controls="main-menu-panel"
            tapTargetExtension="16px"
            onClick={() => setMenuOpen((v) => !v)}
          >
            <HamburgerToggle fillColor={COLORS.tealPrimary} open={menuOpen} />
          </MenuButton>
        </RightGroup>
      </NavContainer>

      <Backdrop
        hidden={!menuOpen}
        aria-hidden="true"
        onClick={() => setMenuOpen(false)}
      />
      <SidePanel
        open={menuOpen}
        role="dialog"
        aria-modal="true"
        aria-label="Main Menu"
        id="main-menu-panel"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onMenuKeyDown}
      >
        <PanelHeader>
          <CloseButton
            aria-label="Close Menu"
            onClick={() => setMenuOpen(false)}
          >
            <img src={xIcon} alt="Close" width="28" height="28" />
          </CloseButton>
        </PanelHeader>
        <Menu>
          <MenuItem
            href="https://account.clutter.com"
            ref={firstItemRef}
            onClick={() => setMenuOpen(false)}
          >
            Log In
          </MenuItem>
          <MenuItem href="/help" onClick={() => setMenuOpen(false)}>
            Help Center
          </MenuItem>
        </Menu>
      </SidePanel>
    </>
  );
};
